import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { Tag, Plus, Trash2, X, ChevronDown, ChevronRight, Loader2, Info } from 'lucide-react';
import { toast } from 'react-toastify';

// ── GraphQL ──────────────────────────────────────────────────────────────────
const GET_SUBCATEGORIES = gql`
  query GetProductSubcategories {
    productSubcategories {
      id
      name
      category
      createdAt
    }
  }
`;

const CREATE_SUBCATEGORY = gql`
  mutation CreateProductSubcategory($name: String!, $category: String) {
    createProductSubcategory(name: $name, category: $category) {
      id
      name
      category
      createdAt
    }
  }
`;

const DELETE_SUBCATEGORY = gql`
  mutation DeleteProductSubcategory($id: ID!) {
    deleteProductSubcategory(id: $id)
  }
`;

// ── Static category definitions ──────────────────────────────────────────────
const PRODUCT_CATEGORIES = [
  { key: 'SOINS_HYGIENE_CORPORELLE',             label: 'Soins & Hygiène Corporelle' },
  { key: 'HYGIENE_BUCCO_DENTAIRE',               label: 'Hygiène Bucco-Dentaire' },
  { key: 'SOINS_VISAGE_DERMOCOSMETIQUE',         label: 'Soins Visage & Dermocosmetique' },
  { key: 'PRODUITS_SOLAIRES',                    label: 'Produits Solaires' },
  { key: 'PRODUITS_BEBE_MATERNITE',              label: 'Produits Bébé & Maternité' },
  { key: 'SOINS_CAPILLAIRES',                    label: 'Soins Capillaires' },
  { key: 'COMPLEMENTS_ALIMENTAIRES_NUTRITION',   label: 'Compléments Alimentaires & Nutrition' },
  { key: 'BIEN_ETRE_SANTE_NATURELLE',            label: 'Bien-être & Santé Naturelle' },
  { key: 'HYGIENE_INTIME_PROTECTIONS',           label: 'Hygiène Intime & Protections' },
  { key: 'MATERIEL_MEDICAL_PREMIERS_SOINS',      label: 'Matériel Médical & Premiers Soins' },
  { key: 'HYGIENE_DESINFECTION',                 label: 'Hygiène & Désinfection' },
  { key: 'COSMETIQUE_MAQUILLAGE_DERMOCOSMETIQUE', label: 'Cosmétique, Maquillage & Dermocosmetique' },
  { key: 'PRODUITS_TROUBLES_SPECIFIQUES',        label: 'Produits pour Troubles Spécifiques' },
];

// ── Suggested subcategories per system category ──────────────────────────────
const SUGGESTED_SUBCATEGORIES = {
  SOINS_HYGIENE_CORPORELLE:           ['Gel douche', 'Savon corps', 'Lait hydratant', 'Crème corps', 'Huile corps', 'Déodorant', 'Gant de bain'],
  HYGIENE_BUCCO_DENTAIRE:             ['Dentifrice', 'Brosse à dents', 'Bain de bouche', 'Fil dentaire', 'Blanchiment dentaire', 'Brossette interdentaire'],
  SOINS_VISAGE_DERMOCOSMETIQUE:       ['Crème hydratante visage', 'Sérum visage', 'Nettoyant visage', 'Contour des yeux', 'Masque visage', 'Démaquillant'],
  PRODUITS_SOLAIRES:                  ['Crème solaire SPF30', 'Crème solaire SPF50', 'Spray solaire', 'Après-soleil', 'Auto-bronzant'],
  PRODUITS_BEBE_MATERNITE:            ['Couches', 'Lingettes bébé', 'Shampoing bébé', 'Crème change', 'Lait maternisé', 'Eau florale bébé', 'Sucette'],
  SOINS_CAPILLAIRES:                  ['Shampoing', 'Après-shampoing', 'Masque capillaire', 'Huile cheveux', 'Soin anti-chute', 'Sérum cheveux'],
  COMPLEMENTS_ALIMENTAIRES_NUTRITION: ['Vitamines', 'Minéraux', 'Probiotiques', 'Oméga 3', 'Magnésium', 'Vitamine C', 'Vitamine D', 'Fer', 'Zinc', 'Collagène'],
  BIEN_ETRE_SANTE_NATURELLE:          ['Huiles essentielles', 'Plantes médicinales', 'Homéopathie', 'Tisanes', 'Aromathérapie', 'Phytothérapie', 'Mélatonine'],
  HYGIENE_INTIME_PROTECTIONS:         ['Gel intime', 'Savon intime', 'Serviettes hygiéniques', 'Tampons', 'Protège-slips', 'Coupe menstruelle'],
  MATERIEL_MEDICAL_PREMIERS_SOINS:    ['Thermomètre', 'Pansements', 'Bandages', 'Oxymètre', 'Tensiomètre', 'Alcool médical', 'Désinfectant', 'Seringue'],
  HYGIENE_DESINFECTION:               ['Gel hydroalcoolique', 'Masques chirurgicaux', 'Masques FFP2', 'Gants en latex', 'Désinfectant surfaces', 'Lingettes désinfectantes'],
  COSMETIQUE_MAQUILLAGE_DERMOCOSMETIQUE: ['Rouge à lèvres', 'Mascara', 'Fond de teint', 'Fard à joues', 'Eye-liner', 'Parfum', 'Correcteur', 'Poudre'],
  PRODUITS_TROUBLES_SPECIFIQUES:      ['Anti-acné', 'Soin eczéma', 'Soin psoriasis', 'Soin rosacea', 'Peaux sensibles', 'Cicatrisant', 'Antiprurigineux'],
};

const CATEGORY_COLORS = [
  { bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-700' },
  { bg: 'bg-green-50',  border: 'border-green-200',  text: 'text-green-700' },
  { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700' },
  { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700' },
  { bg: 'bg-pink-50',   border: 'border-pink-200',   text: 'text-pink-700' },
  { bg: 'bg-teal-50',   border: 'border-teal-200',   text: 'text-teal-700' },
  { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700' },
  { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700' },
  { bg: 'bg-red-50',    border: 'border-red-200',    text: 'text-red-700' },
  { bg: 'bg-cyan-50',   border: 'border-cyan-200',   text: 'text-cyan-700' },
  { bg: 'bg-lime-50',   border: 'border-lime-200',   text: 'text-lime-700' },
  { bg: 'bg-fuchsia-50',border: 'border-fuchsia-200',text: 'text-fuchsia-700' },
  { bg: 'bg-amber-50',  border: 'border-amber-200',  text: 'text-amber-700' },
];

// ── Component ─────────────────────────────────────────────────────────────────
const ProductCategoriesSettings = () => {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [addSubModal, setAddSubModal] = useState({ open: false, forCategory: null });
  const [newSubName, setNewSubName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [addCategoryModal, setAddCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [bulkCreating, setBulkCreating] = useState(null); // catKey being bulk-filled

  // ── Data fetching ──
  const { data, loading, error, refetch } = useQuery(GET_SUBCATEGORIES, {
    fetchPolicy: 'network-only',
  });
  const subcategories = data?.productSubcategories ?? [];

  // ── Mutations ──
  const [createSub, { loading: creating }] = useMutation(CREATE_SUBCATEGORY, {
    onCompleted: () => {
      toast.success('Sous-catégorie ajoutée');
      setAddSubModal({ open: false, forCategory: null });
      setNewSubName('');
      refetch();
    },
    onError: (e) => toast.error(`Erreur: ${e.message}`),
  });

  const [deleteSub, { loading: deleting }] = useMutation(DELETE_SUBCATEGORY, {
    onCompleted: () => {
      toast.success(
        deleteConfirm?.category === '__custom__'
          ? 'Catégorie supprimée'
          : 'Sous-catégorie supprimée',
      );
      setDeleteConfirm(null);
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  // ── Helpers ──
  const toggleCategory = (key) =>
    setExpandedCategories(prev => ({ ...prev, [key]: !prev[key] }));

  const getSubsForCategory = (catKey) =>
    subcategories.filter(s => s.category === catKey);

  // Custom categories are stored as subcategories with category === '__custom__'
  const customCategories = subcategories.filter(s => s.category === '__custom__');

  // Subcategories under a custom category use the custom category's id as their category field
  const getSubsForCustomCategory = (customCatId) =>
    subcategories.filter(s => s.category === customCatId);

  // Orphaned subcategories: category is null/undefined or doesn't match any known key
  const knownCategoryKeys = new Set([
    ...PRODUCT_CATEGORIES.map(c => c.key),
    '__custom__',
    ...customCategories.map(c => c.id),
  ]);
  const orphanedSubcategories = subcategories.filter(
    s => !knownCategoryKeys.has(s.category ?? '')
  );

  const handleBulkCreate = async (catKey) => {
    const suggested = SUGGESTED_SUBCATEGORIES[catKey] ?? [];
    if (suggested.length === 0) return;
    // Only create those that don't already exist
    const existing = new Set(subcategories.map(s => s.name.toLowerCase()));
    const toCreate = suggested.filter(name => !existing.has(name.toLowerCase()));
    if (toCreate.length === 0) { toast.info('Toutes les valeurs par défaut existent déjà.'); return; }
    setBulkCreating(catKey);
    try {
      for (const name of toCreate) {
        await createSub({ variables: { name, category: catKey } });
      }
      toast.success(`${toCreate.length} sous-catégorie(s) ajoutée(s)`);
      refetch();
    } catch (_) {
      // individual errors already toasted by useMutation onError
    } finally {
      setBulkCreating(null);
    }
  };

  const handleAddCustomCategory = () => {
    if (!newCategoryName.trim()) { toast.warning('Nom requis'); return; }
    createSub({ variables: { name: newCategoryName.trim(), category: '__custom__' } });
    setAddCategoryModal(false);
    setNewCategoryName('');
  };

  const handleAddSub = () => {
    if (!newSubName.trim()) { toast.warning('Nom requis'); return; }
    createSub({ variables: { name: newSubName.trim(), category: addSubModal.forCategory || undefined } });
  };

  return (
    <div className="space-y-6">
      {/* Info banner */}
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4">
        <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-700">
          Les <strong>catégories</strong> incluent 13 catégories système et des catégories personnalisées que vous pouvez créer.
          Les <strong>sous-catégories</strong> sont gérées dynamiquement et associées à une catégorie.
        </p>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
          <Info className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-700">Erreur de chargement des sous-catégories</p>
            <p className="text-xs text-red-500 mt-0.5">{error.message}</p>
            <button onClick={() => refetch()} className="mt-2 text-xs text-red-700 underline">Réessayer</button>
          </div>
        </div>
      )}

      {/* ── Categories ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">
            Catégories
            <span className="ml-2 px-2 py-0.5 text-xs font-normal bg-gray-100 text-gray-500 rounded-full">
              {PRODUCT_CATEGORIES.length + customCategories.length}
            </span>
          </h3>
          <button
            onClick={() => { setAddCategoryModal(true); setNewCategoryName(''); }}
            className="flex items-center gap-1 text-xs font-medium text-green-700 hover:text-green-800 border border-green-200 bg-green-50 rounded-lg px-3 py-1.5"
          >
            <Plus size={13} /> Nouvelle catégorie
          </button>
        </div>

        <div className="space-y-1.5">
          {PRODUCT_CATEGORIES.map((cat, i) => {
            const color = CATEGORY_COLORS[i % CATEGORY_COLORS.length];
            const subs = getSubsForCategory(cat.key);
            const isOpen = expandedCategories[cat.key];

            return (
              <div key={cat.key} className={`rounded-xl border ${color.border} overflow-hidden`}>
                {/* Category row */}
                <div className={`flex items-center gap-3 px-4 py-3 ${color.bg}`}>
                  <button
                    onClick={() => toggleCategory(cat.key)}
                    className={`flex-shrink-0 ${color.text}`}
                  >
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  <Tag size={13} className={`flex-shrink-0 ${color.text}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${color.text}`}>{cat.label}</p>
                    <p className="text-xs text-gray-400 font-mono">{cat.key}</p>
                  </div>
                  {subs.length > 0 && (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${color.bg} ${color.text} border ${color.border}`}>
                      {subs.length} sous-cat.
                    </span>
                  )}
                  <button
                    onClick={() => { setAddSubModal({ open: true, forCategory: cat.key }); setNewSubName(''); }}
                    className={`flex items-center gap-1 text-xs font-medium ${color.text} hover:opacity-70 flex-shrink-0`}
                    title="Ajouter une sous-catégorie"
                  >
                    <Plus size={13} /> Ajouter
                  </button>
                </div>

                {/* Subcategories list */}
                {isOpen && (
                  <div className="bg-white border-t border-gray-100 divide-y divide-gray-50">
                    {subs.length === 0 ? (
                      <div className="px-10 py-3 flex items-center justify-between">
                        <p className="text-xs text-gray-400 italic">Aucune sous-catégorie</p>
                        {SUGGESTED_SUBCATEGORIES[cat.key] && (
                          <button
                            onClick={() => handleBulkCreate(cat.key)}
                            disabled={bulkCreating === cat.key}
                            className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg border transition-colors ${color.text} ${color.border} ${color.bg} hover:opacity-80 disabled:opacity-50`}
                          >
                            {bulkCreating === cat.key
                              ? <Loader2 size={11} className="animate-spin" />
                              : <Plus size={11} />}
                            Pré-remplir
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        {subs.map(sub => (
                          <div key={sub.id} className="flex items-center gap-2 px-10 py-2.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                            <span className="text-sm text-gray-700 flex-1">{sub.name}</span>
                            <button
                              onClick={() => setDeleteConfirm(sub)}
                              className="text-gray-300 hover:text-red-500 transition-colors"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        ))}
                        {SUGGESTED_SUBCATEGORIES[cat.key] && (
                          <div className="px-10 py-2 flex justify-end">
                            <button
                              onClick={() => handleBulkCreate(cat.key)}
                              disabled={bulkCreating === cat.key}
                              className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg border transition-colors ${color.text} ${color.border} opacity-60 hover:opacity-100 disabled:opacity-30`}
                            >
                              {bulkCreating === cat.key
                                ? <Loader2 size={10} className="animate-spin" />
                                : <Plus size={10} />}
                              Compléter par défaut
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Custom categories ── */}
        {customCategories.length > 0 && (
          <div className="mt-1.5 space-y-1.5">
            {customCategories.map((cat, i) => {
              const color = CATEGORY_COLORS[(PRODUCT_CATEGORIES.length + i) % CATEGORY_COLORS.length];
              const subs = getSubsForCustomCategory(cat.id);
              const isOpen = expandedCategories[cat.id];
              return (
                <div key={cat.id} className={`rounded-xl border ${color.border} overflow-hidden`}>
                  <div className={`flex items-center gap-3 px-4 py-3 ${color.bg}`}>
                    <button onClick={() => toggleCategory(cat.id)} className={`flex-shrink-0 ${color.text}`}>
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                    <Tag size={13} className={`flex-shrink-0 ${color.text}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold ${color.text}`}>{cat.name}</p>
                      <p className="text-xs text-gray-400">Catégorie personnalisée</p>
                    </div>
                    {subs.length > 0 && (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${color.bg} ${color.text} border ${color.border}`}>
                        {subs.length} sous-cat.
                      </span>
                    )}
                    <button
                      onClick={() => { setAddSubModal({ open: true, forCategory: cat.id }); setNewSubName(''); }}
                      className={`flex items-center gap-1 text-xs font-medium ${color.text} hover:opacity-70 flex-shrink-0`}
                    >
                      <Plus size={13} /> Ajouter
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(cat)}
                      className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0"
                      title="Supprimer cette catégorie"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                  {isOpen && (
                    <div className="bg-white border-t border-gray-100 divide-y divide-gray-50">
                      {subs.length === 0 ? (
                        <p className="px-10 py-3 text-xs text-gray-400 italic">Aucune sous-catégorie</p>
                      ) : (
                        subs.map(sub => (
                          <div key={sub.id} className="flex items-center gap-2 px-10 py-2.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                            <span className="text-sm text-gray-700 flex-1">{sub.name}</span>
                            <button onClick={() => setDeleteConfirm(sub)} className="text-gray-300 hover:text-red-500 transition-colors">
                              <Trash2 size={13} />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Orphaned subcategories (category null or unknown) ── */}
      {orphanedSubcategories.length > 0 && (
        <div className="mt-3 rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 bg-gray-50">
            <Info size={13} className="flex-shrink-0 text-gray-400" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-600">Non classées</p>
              <p className="text-xs text-gray-400">Sous-catégories sans catégorie assignée</p>
            </div>
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">
              {orphanedSubcategories.length} sous-cat.
            </span>
          </div>
          <div className="bg-white border-t border-gray-100 divide-y divide-gray-50">
            {orphanedSubcategories.map(sub => (
              <div key={sub.id} className="flex items-center gap-2 px-10 py-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                <span className="text-sm text-gray-700 flex-1">{sub.name}</span>
                <button
                  onClick={() => setDeleteConfirm(sub)}
                  className="text-gray-300 hover:text-red-500 transition-colors"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Add subcategory modal ── */}
      {addSubModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/30" onClick={() => setAddSubModal({ open: false, forCategory: null })} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">Nouvelle sous-catégorie</h3>
              <button onClick={() => setAddSubModal({ open: false, forCategory: null })} className="text-gray-400 hover:text-gray-600">
                <X size={16} />
              </button>
            </div>

            {addSubModal.forCategory && (
              <div className="mb-4 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-700">
                  Associée à : <strong>
                    {PRODUCT_CATEGORIES.find(c => c.key === addSubModal.forCategory)?.label}
                  </strong>
                </p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
              <input
                type="text"
                value={newSubName}
                onChange={e => setNewSubName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddSub()}
                placeholder="Ex: Shampoing, Crème solaire..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setAddSubModal({ open: false, forCategory: null })}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={handleAddSub}
                disabled={creating || !newSubName.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add category modal ── */}
      {addCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/30" onClick={() => setAddCategoryModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">Nouvelle catégorie</h3>
              <button onClick={() => setAddCategoryModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={16} />
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la catégorie</label>
              <input
                type="text"
                value={newCategoryName}
                onChange={e => setNewCategoryName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddCustomCategory()}
                placeholder="Ex: Produits Bio, Soins Spéciaux..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setAddCategoryModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={handleAddCustomCategory}
                disabled={creating || !newCategoryName.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirm ── */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/30" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-2">Supprimer la sous-catégorie</h3>
            <p className="text-sm text-gray-600 mb-5">
              Supprimer <strong>"{deleteConfirm.name}"</strong> ? Cette action est irréversible.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={() => deleteSub({ variables: { id: deleteConfirm.id } })}
                disabled={deleting}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCategoriesSettings;
